import React, { useEffect, useRef, useState } from "react";
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, Paper, Snackbar, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import SaveIcon from "@mui/icons-material/Save";
import UndoIcon from "@mui/icons-material/Undo";
import { addDoc, collection, doc, getDocs, limit, query, Timestamp, updateDoc } from "firebase/firestore";
import Navbar from "../Components/Navbar/navbar";
import HistoricoCadastro from "../Components/HistoricoCadastro/HistoricoCadastro";
import { auth, db } from "../Config/firebase";
import { aplicarTenantAtual, consultaTenantAtual } from "../Utils/tenant";
import { CAMPOS_RISCO, COLLECTION_MATRIZ_RISCOS, novoRisco, RISCOS_INICIAIS } from "./matrizRiscosData";
import RelatorioMatrizRiscosPrint from "./RelatorioMatrizRiscosPrint";

const cabecalho = { bgcolor: "#0b4775", color: "white", border: "1px solid #6b7280", fontWeight: 800, textAlign: "center" };
const celula = { border: "1px solid #557a4c", bgcolor: "white", color: "#2f256f", textAlign: "center", whiteSpace: "normal", overflowWrap: "break-word", lineHeight: 1.25, py: 0.65, cursor: "text", verticalAlign: "middle" };
const larguras = { processo: 150, origemRisco: 210, riscoIdentificado: 240, causa: 250, efeitoPotencial: 250, classificacao: 140, nivelRisco: 110, acaoControle: 300, statusAcoes: 140 };
const estiloPorCampo = {
  processo: { color: "#7c1d0d", fontWeight: 800 },
  origemRisco: { color: "#073b66", fontWeight: 800 },
  riscoIdentificado: { color: "#073b66", fontWeight: 800 },
  causa: { color: "#49387e" },
  efeitoPotencial: { color: "#273b8f" },
  classificacao: { color: "#342b78" },
  nivelRisco: { color: "#342b78" },
  acaoControle: { color: "#342b78" },
};
const coresStatus = {
  Realizado: { bgcolor: "#16854b", color: "white" },
  "Em andamento": { bgcolor: "#d97706", color: "white" },
  Planejado: { bgcolor: "#2563a9", color: "white" },
  Cancelado: { bgcolor: "#b91c1c", color: "white" },
};

function montarCadastro(id, dados) {
  return {
    id,
    revisao: dados.revisao === "00" ? "" : dados.revisao || "",
    atualizadoEmDocumento: dados.atualizadoEmDocumento || "",
    criadoEm: dados.criadoEm || null,
    criadoPor: dados.criadoPor || null,
    criadoPorNome: dados.criadoPorNome || "",
    criadoPorEmail: dados.criadoPorEmail || "",
    atualizadoEm: dados.atualizadoEm || null,
    atualizadoPor: dados.atualizadoPor || null,
    atualizadoPorNome: dados.atualizadoPorNome || "",
    atualizadoPorEmail: dados.atualizadoPorEmail || "",
    historico: Array.isArray(dados.historico) ? dados.historico : [],
    riscos: (dados.riscos || []).map((item, indice) => ({ ...novoRisco(indice + 1), ...item, item: indice + 1 })),
  };
}

function copiarCadastro(cadastro) {
  return cadastro ? {
    ...cadastro,
    riscos: cadastro.riscos.map((item) => ({ ...item })),
    historico: [...(cadastro.historico || [])],
  } : null;
}

export default function DetalhesMatrizRiscos() {
  const criandoRef = useRef(false);
  const tabelaRef = useRef(null);
  const barraInferiorRef = useRef(null);
  const cadastroSalvoRef = useRef(null);
  const [cadastro, setCadastro] = useState(null);
  const [semCadastro, setSemCadastro] = useState(false);
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [alterado, setAlterado] = useState(false);
  const [versaoTabela, setVersaoTabela] = useState(0);
  const [larguraRolagem, setLarguraRolagem] = useState(1900);
  const [indiceExclusao, setIndiceExclusao] = useState(null);
  const [modoRelatorio, setModoRelatorio] = useState(false);
  const [notificacao, setNotificacao] = useState({ aberta: false, mensagem: "", tipo: "success" });

  useEffect(() => { (async () => {
    setCarregando(true);
    try {
      const snapshot = await getDocs(query(consultaTenantAtual(collection(db, COLLECTION_MATRIZ_RISCOS)), limit(1)));
      if (snapshot.empty) setSemCadastro(true);
      else {
        const carregado = montarCadastro(snapshot.docs[0].id, snapshot.docs[0].data());
        cadastroSalvoRef.current = copiarCadastro(carregado);
        setCadastro(carregado);
      }
    } catch (erro) {
      console.error("Erro ao carregar matriz de riscos:", erro);
      setNotificacao({ aberta: true, mensagem: "Não foi possível carregar a matriz de riscos.", tipo: "error" });
    } finally { setCarregando(false); }
  })(); }, []);

  useEffect(() => {
    function atualizarLarguraRolagem() {
      const tabela = tabelaRef.current;
      const barra = barraInferiorRef.current;
      if (!tabela || !barra) return;
      const diferencaConteineres = Math.max(0, barra.clientWidth - tabela.clientWidth);
      setLarguraRolagem(tabela.scrollWidth + diferencaConteineres + 8);
    }
    const quadro = window.requestAnimationFrame(atualizarLarguraRolagem);
    window.addEventListener("resize", atualizarLarguraRolagem);
    return () => {
      window.cancelAnimationFrame(quadro);
      window.removeEventListener("resize", atualizarLarguraRolagem);
    };
  }, [cadastro?.riscos.length, versaoTabela]);

  async function cadastrarDadosExistentes() {
    if (criandoRef.current) return;
    criandoRef.current = true;
    setSalvando(true);
    try {
      const agora = Timestamp.now();
      const usuario = {
        uid: auth.currentUser?.uid || "",
        nome: auth.currentUser?.displayName || auth.currentUser?.email || "Não informado",
        email: auth.currentUser?.email || "",
      };
      const dados = aplicarTenantAtual({
        codigoDocumento: "RQ 1.6",
        revisao: "",
        atualizadoEmDocumento: "",
        riscos: RISCOS_INICIAIS.map((item) => ({ ...item })),
        criadoEm: agora,
        criadoPor: usuario.uid,
        criadoPorNome: usuario.nome,
        criadoPorEmail: usuario.email,
        atualizadoEm: agora,
        atualizadoPor: usuario.uid,
        atualizadoPorNome: usuario.nome,
        atualizadoPorEmail: usuario.email,
        historico: [{ acao: "CRIACAO", descricao: "Matriz de riscos cadastrada com os dados existentes.", usuario, data: agora }],
      });
      const criado = await addDoc(collection(db, COLLECTION_MATRIZ_RISCOS), dados);
      const criadoLocal = montarCadastro(criado.id, dados);
      cadastroSalvoRef.current = copiarCadastro(criadoLocal);
      setCadastro(criadoLocal);
      setSemCadastro(false);
      setAlterado(false);
      setNotificacao({ aberta: true, mensagem: "Matriz de riscos cadastrada com sucesso.", tipo: "success" });
    } catch (erro) {
      console.error("Erro ao cadastrar matriz de riscos:", erro);
      setNotificacao({ aberta: true, mensagem: "Não foi possível cadastrar a matriz de riscos.", tipo: "error" });
    } finally {
      criandoRef.current = false;
      setSalvando(false);
    }
  }

  function atualizarCadastro(campo, valor) {
    setCadastro((atual) => {
      if (atual[campo] === valor) return atual;
      setAlterado(true);
      return { ...atual, [campo]: valor };
    });
  }
  function atualizarRisco(indice, campo, valor) {
    setCadastro((atual) => {
      if (String(atual.riscos[indice]?.[campo] || "") === valor) return atual;
      setAlterado(true);
      return { ...atual, riscos: atual.riscos.map((item, posicao) => posicao === indice ? { ...item, [campo]: valor } : item) };
    });
  }
  function adicionarRisco() {
    setCadastro((atual) => ({ ...atual, riscos: [...atual.riscos, novoRisco(atual.riscos.length + 1)] }));
    setAlterado(true);
  }
  function removerRisco(indice) {
    setCadastro((atual) => ({ ...atual, riscos: atual.riscos.filter((_, posicao) => posicao !== indice).map((item, posicao) => ({ ...item, item: posicao + 1 })) }));
    setAlterado(true);
    setIndiceExclusao(null);
  }

  function fecharConfirmacaoExclusao(evento) {
    evento?.stopPropagation?.();
    setIndiceExclusao(null);
  }

  function confirmarExclusao(evento) {
    evento?.stopPropagation?.();
    if (indiceExclusao !== null) removerRisco(indiceExclusao);
  }

  function cancelarAlteracoes() {
    if (!alterado || !cadastroSalvoRef.current) return;
    setCadastro(copiarCadastro(cadastroSalvoRef.current));
    setAlterado(false);
    setVersaoTabela((atual) => atual + 1);
    setNotificacao({ aberta: true, mensagem: "Alterações canceladas.", tipo: "info" });
  }

  async function salvar() {
    if (cadastro.riscos.some((item) => !String(item.processo || "").trim() || !String(item.riscoIdentificado || "").trim())) {
      setNotificacao({ aberta: true, mensagem: "Informe o processo e o risco identificado em todas as linhas.", tipo: "warning" });
      return;
    }
    setSalvando(true);
    try {
      const riscos = cadastro.riscos.map((item, indice) => ({ item: indice + 1, ...Object.fromEntries(CAMPOS_RISCO.map(([campo]) => [campo, String(item[campo] || "").trim()])) }));
      const agora = Timestamp.now();
      const usuario = {
        uid: auth.currentUser?.uid || "",
        nome: auth.currentUser?.displayName || auth.currentUser?.email || "Não informado",
        email: auth.currentUser?.email || "",
      };
      const historicoAnterior = cadastro.historico?.length
        ? cadastro.historico
        : cadastro.criadoEm
          ? [{
              acao: "CRIACAO",
              descricao: "Matriz de riscos cadastrada no sistema.",
              usuario: {
                uid: cadastro.criadoPor || "",
                nome: cadastro.criadoPorNome || "Não informado",
                email: cadastro.criadoPorEmail || "",
              },
              data: cadastro.criadoEm,
            }]
          : [];
      const historico = [...historicoAnterior, { acao: "ALTERACAO", descricao: "Alterações salvas na matriz de riscos.", usuario, data: agora }];
      await updateDoc(doc(db, COLLECTION_MATRIZ_RISCOS, cadastro.id), {
        revisao: cadastro.revisao.trim(),
        atualizadoEmDocumento: cadastro.atualizadoEmDocumento.trim(),
        riscos,
        atualizadoEm: agora,
        atualizadoPor: usuario.uid,
        atualizadoPorNome: usuario.nome,
        atualizadoPorEmail: usuario.email,
        historico,
      });
      const cadastroAtualizado = { ...cadastro, riscos, atualizadoEm: agora, atualizadoPor: usuario.uid, atualizadoPorNome: usuario.nome, atualizadoPorEmail: usuario.email, historico };
      cadastroSalvoRef.current = copiarCadastro(cadastroAtualizado);
      setCadastro(cadastroAtualizado);
      setAlterado(false);
      setNotificacao({ aberta: true, mensagem: "Matriz de riscos salva com sucesso.", tipo: "success" });
    } catch (erro) {
      console.error("Erro ao salvar matriz de riscos:", erro);
      setNotificacao({ aberta: true, mensagem: "Não foi possível salvar a matriz de riscos.", tipo: "error" });
    } finally { setSalvando(false); }
  }

  if (modoRelatorio && cadastro) return <RelatorioMatrizRiscosPrint cadastro={cadastro} onVoltar={() => setModoRelatorio(false)} />;

  return <><Navbar /><Box sx={{ p: { xs: 1, md: 3 }, mt: 6, pb: 5 }}>
    {carregando && <Box sx={{ p: 4, textAlign: "center" }}><CircularProgress size={24} /></Box>}
    {!carregando && semCadastro && <Paper elevation={0} className="cabecalho-lista-cadastro" sx={{ p: 2 }}><Typography variant="h5" fontWeight={700}>RQ 1.6 — Matriz de Riscos</Typography><Typography color="text.secondary" sx={{ my: 2 }}>A matriz é única por empresa. Cadastre os 52 riscos existentes para iniciar.</Typography><Button variant="contained" onClick={cadastrarDadosExistentes} disabled={salvando}>{salvando ? "Cadastrando..." : "Cadastrar dados existentes"}</Button></Paper>}
    {cadastro && <><Paper elevation={0} className="cabecalho-lista-cadastro" sx={{ p: 2, mb: 1.5, overflowX: "auto" }}><Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 1470, whiteSpace: "nowrap" }}><Typography variant="h5" fontWeight={700} sx={{ mr: "auto" }}>RQ 1.6 — Matriz de Riscos</Typography><TextField size="small" label="Revisão" value={cadastro.revisao} onChange={(event) => atualizarCadastro("revisao", event.target.value)} placeholder="Alterar número da revisão" sx={{ width: 210, flexShrink: 0 }} /><TextField size="small" label="Atualizado em" value={cadastro.atualizadoEmDocumento} onChange={(event) => atualizarCadastro("atualizadoEmDocumento", event.target.value)} placeholder="DD/MM/AAAA" sx={{ width: 180, flexShrink: 0 }} /><HistoricoCadastro colecao={COLLECTION_MATRIZ_RISCOS} documentoId={cadastro.id} registro={cadastro} /><Button variant="outlined" startIcon={<PictureAsPdfIcon />} onClick={() => setModoRelatorio(true)} sx={{ flexShrink: 0 }}>Exportar PDF</Button><Button variant="outlined" startIcon={<AddIcon />} onClick={adicionarRisco} sx={{ flexShrink: 0 }}>Adicionar risco</Button><Button variant="outlined" color="inherit" startIcon={<UndoIcon />} onClick={cancelarAlteracoes} disabled={salvando || !alterado} sx={{ flexShrink: 0 }}>Cancelar</Button><Button variant="contained" startIcon={<SaveIcon />} onClick={salvar} disabled={salvando || !alterado} sx={{ flexShrink: 0 }}>{salvando ? "Salvando..." : "Salvar"}</Button></Stack></Paper>
    <Paper ref={tabelaRef} onScroll={(event) => { if (barraInferiorRef.current) barraInferiorRef.current.scrollLeft = event.currentTarget.scrollLeft; }} variant="outlined" sx={{ overflowX: "auto", width: "100%", scrollbarWidth: "none", "&::-webkit-scrollbar": { display: "none" } }}><Table key={versaoTabela} size="small" sx={{ minWidth: 1900, tableLayout: "fixed" }}><TableHead><TableRow><TableCell sx={{ ...cabecalho, width: 60 }}>Item</TableCell>{CAMPOS_RISCO.map(([campo, titulo]) => <TableCell key={campo} sx={{ ...cabecalho, width: larguras[campo] }}>{titulo}</TableCell>)}<TableCell sx={{ ...cabecalho, width: 55 }} /></TableRow></TableHead><TableBody>{cadastro.riscos.map((item, indice) => <TableRow key={indice}><TableCell sx={{ ...celula, color: "#003bca", fontWeight: 700, width: 60, whiteSpace: "nowrap" }}>{indice + 1}</TableCell>{CAMPOS_RISCO.map(([campo]) => campo === "statusAcoes" ? <TableCell key={campo} sx={{ ...celula, width: larguras[campo], cursor: "default", px: 1 }}><Box component="select" aria-label={`Status das ações do item ${indice + 1}`} value={item.statusAcoes || ""} onChange={(event) => atualizarRisco(indice, campo, event.target.value)} sx={{ width: "100%", border: 0, borderRadius: 10, px: 1, py: 0.25, outline: 0, cursor: "pointer", fontSize: 12, fontWeight: 700, textAlign: "center", ...((coresStatus[item.statusAcoes] || { bgcolor: "#d1d5db", color: "#374151" })) }}><option value="">Selecione</option><option value="Planejado">Planejado</option><option value="Em andamento">Em andamento</option><option value="Realizado">Realizado</option><option value="Cancelado">Cancelado</option></Box></TableCell> : <TableCell key={campo} contentEditable suppressContentEditableWarning sx={{ ...celula, ...estiloPorCampo[campo], width: larguras[campo] }} onInput={() => setAlterado(true)} onBlur={(event) => atualizarRisco(indice, campo, event.currentTarget.innerText)}>{item[campo] || ""}</TableCell>)}<TableCell sx={{ ...celula, cursor: "default" }}><Tooltip title="Remover risco"><IconButton size="small" color="error" onClick={(evento) => { evento.stopPropagation(); setIndiceExclusao(indice); }}><DeleteOutlineIcon fontSize="small" /></IconButton></Tooltip></TableCell></TableRow>)}</TableBody></Table></Paper>
    <Box ref={barraInferiorRef} onScroll={(event) => { if (tabelaRef.current) tabelaRef.current.scrollLeft = event.currentTarget.scrollLeft; }} sx={{ position: "fixed", bottom: 0, left: 0, width: "100vw", height: 20, overflowX: "auto", overflowY: "hidden", bgcolor: "background.paper", borderTop: "1px solid #94a3b8", zIndex: 1300 }}><Box sx={{ width: larguraRolagem, height: 1 }} /></Box>
    </>}
  </Box><Dialog open={indiceExclusao !== null} onClose={fecharConfirmacaoExclusao} onClick={(evento) => evento.stopPropagation()}><DialogTitle>Excluir risco</DialogTitle><DialogContent><DialogContentText>Tem certeza que deseja excluir este risco?</DialogContentText></DialogContent><DialogActions><Button onClick={fecharConfirmacaoExclusao}>Cancelar</Button><Button color="error" variant="contained" onClick={confirmarExclusao}>Sim</Button></DialogActions></Dialog><Snackbar open={notificacao.aberta} autoHideDuration={5000} onClose={() => setNotificacao((atual) => ({ ...atual, aberta: false }))}><Alert severity={notificacao.tipo} onClose={() => setNotificacao((atual) => ({ ...atual, aberta: false }))}>{notificacao.mensagem}</Alert></Snackbar></>;
}
